import { useEffect, useState } from "react"
import { cn } from "../../lib/utils"
import type { ChatProgress } from "./types"

interface ThinkingIndicatorProps {
  progress?: ChatProgress | null
  /** Compact variant for the chat pill — slightly tighter padding. */
  compact?: boolean
  className?: string
}

const THINKING_WORDS = [
  "Thinking",
  "Pondering",
  "Conjuring",
  "Noodling",
  "Finagling",
  "Brewing",
  "Tinkering",
  "Sketching",
  "Composing",
  "Refining",
  "Crafting",
  "Wrangling",
  "Polishing",
  "Restyling",
  "Percolating",
  "Cogitating",
  "Marinating",
  "Simmering",
  "Designing",
  "Reticulating",
]

// Animated star glyph frames (Claude-Code style: ✶ spinning).
const STAR_FRAMES = ["✶", "✷", "✸", "✹", "✺", "✹", "✸", "✷"]

/**
 * Claude-Code-style thinking line: `✶ Manifesting… (10s)` — a spinning star, a
 * rotating gerund, and an elapsed timer. When the host supplies `progress` (a
 * tool in flight), shows the tool name instead of a random word.
 */
export function ThinkingIndicator({ progress, compact = false, className }: ThinkingIndicatorProps) {
  const [word, setWord] = useState(THINKING_WORDS[0])
  const [elapsed, setElapsed] = useState(0)
  const [frame, setFrame] = useState(0)

  useEffect(() => {
    const start = Date.now()
    const pick = () => THINKING_WORDS[Math.floor(Math.random() * THINKING_WORDS.length)]
    setWord(pick()) // randomise after mount (SSR-safe)
    const wordTimer = setInterval(() => setWord(pick()), 2800)
    const tickTimer = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000)
    const spinTimer = setInterval(() => setFrame((f) => (f + 1) % STAR_FRAMES.length), 220)
    return () => {
      clearInterval(wordTimer)
      clearInterval(tickTimer)
      clearInterval(spinTimer)
    }
  }, [])

  const label = progress ? humanizeToolName(progress.tool) : word

  return (
    <div
      className={cn(
        "flex w-fit items-center gap-2 px-1 text-sm",
        compact ? "py-0.5" : "py-1.5",
        className,
      )}
    >
      <span aria-hidden className="text-base leading-none text-primary">
        {STAR_FRAMES[frame]}
      </span>
      <span className="font-medium text-primary">{label}…</span>
      <span className="tabular-nums text-muted-foreground/70">({elapsed}s)</span>
    </div>
  )
}

/** Soften tool ids for display: `get-deck-html` → `Get deck html`. */
function humanizeToolName(name: string): string {
  const spaced = name
    .replace(/[-_]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
